import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Button, Checkbox, Divider, Grid, List, ListItem, ListItemIcon, ListItemText, Paper, Typography, InputBase } from '@mui/material';
import { ChevronLeft as MoveLeftIcon, ChevronRight as MoveRightIcon, FirstPage as MoveAllLeftIcon, LastPage as MoveAllRightIcon, Search as SearchIcon, Clear as ClearSearchIcon, } from '@mui/icons-material';
const pluralise = (value, length) => {
    let plu = 's';
    if (value.constructor.name === 'Array') {
        return length === 1 ? value[0] : value[1];
    }
    let v = value;
    if (v.endsWith('h')) {
        plu = 'es';
    }
    if (v.endsWith('y')) {
        if (length !== 1) {
            plu = 'ies';
            v = v.slice(0, -1);
        }
    }
    return `${v}${length === 1 ? '' : plu}`;
};
const not = (a, b) => a.filter((value) => b.indexOf(value) === -1);
const intersection = (a, b) => a.filter((value) => b.indexOf(value) !== -1);
const StyledPaper = styled(Paper)(() => ({
    minWidth: 400,
    minHeight: 400,
    maxHeight: 900,
    overflow: 'auto',
}));
const Title = styled(Typography)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
}));
const SearchButton = styled(SearchIcon)(({ theme }) => ({
    cursor: 'pointer',
    width: 24,
    height: 24,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
}));
const ClearSearchButton = styled(ClearSearchIcon)(({ theme }) => ({
    cursor: 'pointer',
    width: 24,
    height: 24,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
}));
const StyledButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(0.5, 0),
}));
const CustomTransferList = ({ labelLeft, labelRight, description, sortingOn = true, selectedItems, allItems, handleTransfer }) => {
    const [checked, setChecked] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState(selectedItems);
    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);
    useEffect(() => {
        setLeft(allItems.filter((i) => !selectedItems.map((e) => e.id).includes(i.id)));
    }, [allItems, selectedItems]);
    useEffect(() => {
        const rightIds = right.map((i) => i.id);
        const currentIds = selectedItems.map((i) => i.id);
        const areSame = currentIds.every((id) => rightIds.includes(id)) && rightIds.every((id) => currentIds.includes(id));
        if (!areSame) {
            handleTransfer(right);
        }
    }, [right, selectedItems, handleTransfer]);
    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];
        if (currentIndex === -1) {
            newChecked.push(value);
        }
        else {
            newChecked.splice(currentIndex, 1);
        }
        setChecked(newChecked);
    };
    const handleAllRight = () => {
        if (searchValue.length > 0) {
            setRight((i) => [...i, ...left.filter((i) => i.label.toLowerCase().includes(searchValue.toLowerCase()))]);
            setLeft(left.filter((i) => !i.label.toLowerCase().includes(searchValue.toLowerCase())));
        }
        else {
            setRight((i) => [...i, ...left]);
            setLeft([]);
        }
        setChecked([]);
    };
    const handleCheckedRight = () => {
        setRight((i) => [...i, ...leftChecked]);
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };
    const handleCheckedLeft = () => {
        setLeft((i) => [...i, ...rightChecked]);
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };
    const handleAllLeft = () => {
        setLeft((i) => [...i, ...right]);
        setRight([]);
        setChecked([]);
    };
    const customList = (title, items) => (_jsxs(StyledPaper, Object.assign({ elevation: 0, variant: "outlined" }, { children: [_jsxs(Title, Object.assign({ variant: "subtitle1" }, { children: [items.length, " ", pluralise(title, items.length)] })), _jsx(Divider, {}), _jsxs(List, Object.assign({ dense: true, component: "div", role: "list" }, { children: [items.sort(sortingOn ? (a, b) => a.label.localeCompare(b.label) : undefined).map((item) => {
                        const labelId = `transfer-list-item-${item.id}-label`;
                        return (_jsxs(ListItem, Object.assign({ role: "listitem", onClick: handleToggle(item) }, { children: [_jsx(ListItemIcon, { children: _jsx(Checkbox, { "data-qa": "transfer-list-item-check-box", checked: checked.indexOf(item) !== -1, color: "primary", tabIndex: -1, disableRipple: true, inputProps: { 'aria-labelledby': labelId } }) }), _jsx(ListItemText, { "data-qa": "transfer-list-item-name", id: labelId, primary: item.label, secondary: item.subLabel })] }), item.id));
                    }), _jsx(ListItem, {})] }))] })));
    return (_jsxs(_Fragment, { children: [_jsxs(Grid, Object.assign({ container: true, spacing: 2, alignItems: "center" }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 5 }, { children: _jsx(Paper, Object.assign({ variant: "outlined", sx: {
                                p: 1,
                                mb: 1,
                            } }, { children: _jsx(InputBase, { sx: { width: '100%' }, placeholder: "Search", value: searchValue, onChange: (event) => setSearchValue(event.target.value || ''), startAdornment: _jsx(SearchButton, {}), endAdornment: _jsx(ClearSearchButton, { onClick: () => setSearchValue('') }), inputProps: { 'aria-label': 'search list' } }) })) })), _jsx(Grid, { item: true, xs: 2 }), _jsx(Grid, Object.assign({ item: true, xs: 5 }, { children: _jsx(Typography, Object.assign({ variant: "caption" }, { children: description })) }))] })), _jsxs(Grid, Object.assign({ container: true, spacing: 2, alignItems: "center" }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 5 }, { children: customList(labelLeft, left.filter((i) => i.label.toLowerCase().includes(searchValue.toLowerCase()))) })), _jsx(Grid, Object.assign({ item: true, xs: 2 }, { children: _jsxs(Grid, Object.assign({ container: true, direction: "column", alignItems: "center" }, { children: [_jsx(StyledButton, Object.assign({ "data-qa": "move-all-right-button", variant: "outlined", size: "small", color: "primary", onClick: handleAllRight, disabled: left.length === 0, "aria-label": "move all right" }, { children: _jsx(MoveAllRightIcon, {}) })), _jsx(StyledButton, Object.assign({ "data-qa": "move-selected-right-button", variant: "outlined", size: "small", color: "primary", onClick: handleCheckedRight, disabled: leftChecked.length === 0, "aria-label": "move selected right" }, { children: _jsx(MoveRightIcon, {}) })), _jsx(StyledButton, Object.assign({ "data-qa": "move-selected-left-button", variant: "outlined", size: "small", color: "primary", onClick: handleCheckedLeft, disabled: rightChecked.length === 0, "aria-label": "move selected left" }, { children: _jsx(MoveLeftIcon, {}) })), _jsx(StyledButton, Object.assign({ "data-qa": "move-all-left-button", variant: "outlined", size: "small", color: "primary", onClick: handleAllLeft, disabled: right.length === 0, "aria-label": "move all left" }, { children: _jsx(MoveAllLeftIcon, {}) }))] })) })), _jsx(Grid, Object.assign({ item: true, xs: 5 }, { children: customList(labelRight, right) }))] }))] }));
};
export default CustomTransferList;
//# sourceMappingURL=index.js.map